import axios, { AxiosInstance, AxiosResponse } from 'axios';

export interface Desconto {
     dataLimite: string;
     valorFixo: number;
     valorPercentual: number;
}

export interface Recebedor {
     identificadorRecebedor: number | string;
     tipoRecebedor: 'Convenio' | 'Participante';
     valorRepasse: number;
}

interface FormasPagamento {
     codigoTipoPagamento: 'PIX' | 'BOLETO';
     quantidadeParcelas: number;
}

interface SolicitacaoASerCriada {
     geral: {
          numeroConvenio: number;
          timestampLimiteSolicitacao: string;
          pagamentoUnico: boolean;
          valorSolicitacao: number;
          codigoConciliacaoSolicitacao: string;
          descricaoSolicitacao: string;
          urlCallback?: string;
     };
     devedor: {
          tipoDocumento: number;
          numeroDocumento: number;
          // Campos opcionais - podem ou não estar presentes
          cep?: number;
          endereco?: string;
          bairro?: string;
          cidade?: string;
          uf?: string;
          email?: string;
          dddTelefone?: number;
          telefone?: number;
     };
     vencimento: {
          data: string;
          multaValorFixo: number;
          multaPercentual: number;
          jurosPercentual: number;
          descontos?: Desconto[];
     };
     formasPagamento: FormasPagamento[];
     repasse: {
          tipoValorRepasse: 'Percentual' | 'Fixo';
          recebedores: Recebedor[];
     };
}

interface SolicitacaoCreditoResponse {
     success: boolean;
     message: string;
     data: {
          id: number;
          numero_solicitacao: number;
          url_pagamento: string;
          codigo_conciliacao: string;
          valor: number;
          status: string;
          limite_pagamento: string;
     };
}

interface ApiErrorResponse {
     success: false;
     error: string;
     details?: any;
}

export type ApiResponse = SolicitacaoCreditoResponse | ApiErrorResponse;

class CantinaApiClient {
     private client: AxiosInstance;

     constructor() {
          // Configuração base do cliente HTTP
          this.client = axios.create({
               baseURL: this.getApiBaseUrl(),
               timeout: 30000,
               headers: {
                    'Content-Type': 'application/json',
               },
          });

          // Interceptor para adicionar autenticação se necessário
          this.client.interceptors.request.use((config) => {
               // TODO: Adicionar token de autenticação quando implementado
               // const token = localStorage.getItem('auth_token');
               // if (token) {
               //   config.headers.Authorization = `Bearer ${token}`;
               // }
               return config;
          });

          // Interceptor para tratamento de erros
          this.client.interceptors.response.use(
               (response) => response,
               (error) => {
                    console.error('Erro na API de Cantina:', {
                         status: error.response?.status,
                         data: error.response?.data,
                         message: error.message,
                    });
                    throw error;
               },
          );
     }

     /**
      * Determina a URL base da API baseada no ambiente
      */
     private getApiBaseUrl(): string {
          const prodUrl = import.meta.env.VITE_CANTINA_API_URL?.trim();

          if (import.meta.env.DEV) {
               return '/cantina-api';
          }

          return prodUrl;
     }

     /**
      * Cria uma nova solicitação de crédito
      */
     async criarSolicitacaoCredito(request: SolicitacaoASerCriada & { fk_id_organizacao: number }): Promise<SolicitacaoCreditoResponse> {
          try {
               console.log('Criando solicitação de crédito:', request);

               const response: AxiosResponse<SolicitacaoCreditoResponse> = await this.client.post('/creditos/solicitar', request);

               console.log('Solicitação criada com sucesso:', response.data);
               return response.data;
          } catch (error: any) {
               console.error('Erro ao criar solicitação de crédito:', error);

               // Retorna erro estruturado
               const errorResponse = error.response?.data || {
                    success: false,
                    error: 'Erro de comunicação com a API',
               };

               throw new Error(errorResponse.error || 'Erro desconhecido');
          }
     }

     /**
      * Consulta status de uma solicitação
      */
     async consultarStatusSolicitacao(numeroSolicitacao: number): Promise<any> {
          try {
               const response = await this.client.get(`/solicitacoes/${numeroSolicitacao}/status-cobranca`);

               return response.data;
          } catch (error: any) {
               console.error('Erro ao consultar status da solicitação:', error);
               throw new Error(error.response?.data?.error || 'Erro ao consultar status');
          }
     }

     /**
      * Lista solicitações por organização
      */
     async listarSolicitacoes(organizacaoId: number): Promise<any[]> {
          try {
               const response = await this.client.get(`/solicitacoes/organizacao/${organizacaoId}`);

               return response.data.data || [];
          } catch (error: any) {
               console.error('Erro ao listar solicitações:', error);
               throw new Error(error.response?.data?.error || 'Erro ao listar solicitações');
          }
     }

     /**
      * Cancela uma solicitação
      */
     async cancelarSolicitacao(numeroSolicitacao: number): Promise<boolean> {
          try {
               const response = await this.client.post(`/solicitacoes/${numeroSolicitacao}/cancelar`);

               return response.data.success || false;
          } catch (error: any) {
               console.error('Erro ao cancelar solicitação:', error);
               throw new Error(error.response?.data?.error || 'Erro ao cancelar solicitação');
          }
     }

     /**
      * Health check da API
      */
     async healthCheck(): Promise<boolean> {
          try {
               const response = await this.client.get('/health');
               return response.status === 200;
          } catch (error) {
               console.error('API de Cantina não está disponível:', error);
               return false;
          }
     }
}

// Instância singleton
const cantinaApiClient = new CantinaApiClient();

export { cantinaApiClient };
export type { SolicitacaoASerCriada, SolicitacaoCreditoResponse };

